/**
 * Spec 044 · una cita vigente es la que aún no ha empezado.
 *
 * Misma regla que `backend/src/metrics/citas-vigentes.ts`. Se copia a
 * propósito: el navegador no importa del API, y dos sitios que comparan
 * `dia`+`inicio` como texto no pueden divergir en silencio.
 */
import { useEffect, useState } from 'react'

export const ZONA_DEFECTO = 'Atlantic/Canary'

export interface RelojPared {
    dia: string
    hora: string
}

export function relojPared(ahora: Date, zona: string = ZONA_DEFECTO): RelojPared {
    const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: zona,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    })
    const partes = Object.fromEntries(fmt.formatToParts(ahora).map((p) => [p.type, p.value]))
    return {
        dia: `${partes['year']}-${partes['month']}-${partes['day']}`,
        hora: `${partes['hour']}:${partes['minute']}`,
    }
}

export function esVigente(cita: { dia: string; inicio: string }, reloj: RelojPared): boolean {
    return cita.dia > reloj.dia || (cita.dia === reloj.dia && cita.inicio > reloj.hora)
}

export function contarVigentes(
    citas: readonly { dia: string; inicio: string }[],
    reloj: RelojPared,
): number {
    return citas.filter((c) => esVigente(c, reloj)).length
}

/** Spec 044 · FR-006: la ventana pintada, con o sin las que ya empezaron. */
export function contarEnVentana(
    citas: readonly { dia: string; inicio: string }[],
    reloj: RelojPared,
    incluirPasadas: boolean,
): number {
    return incluirPasadas ? citas.length : contarVigentes(citas, reloj)
}

export function rotuloReservas(n: number): string {
    return n === 1 ? '1 reserva' : `${n} reservas`
}

/** Se actualiza a menudo: al cambiar el minuto, las que ya empezaron salen. */
export function useRelojPared(zona: string = ZONA_DEFECTO): RelojPared {
    const [reloj, setReloj] = useState(() => relojPared(new Date(), zona))
    useEffect(() => {
        setReloj(relojPared(new Date(), zona))
        const id = setInterval(() => setReloj(relojPared(new Date(), zona)), 15_000)
        return () => clearInterval(id)
    }, [zona])
    return reloj
}
